'use client'

import { CommunicateWithUs } from '@/components/CommunicateWithUs'
import { Button } from '@/components/fun-ui/Button'
import { useBrowser } from '@/hooks/useBrowser'
import { useTrace } from '@/hooks/useTrace'
import { isChunkLoadError, scheduleChunkErrorReload } from '@/lib/chunkErrorRecovery'
import { shouldReportClientError } from '@/lib/errorReportLimiter'
import Image from 'next/image'
import { useEffect } from 'react'

interface ErrorComponentProps {
  error: Error & { digest?: string }
  reset: () => void
}

export default function Error({ error, reset }: ErrorComponentProps) {
  const { error: traceError } = useTrace();
  const browser = useBrowser();
  const pkg = process.env.NEXT_PUBLIC_PACKAGE;

  // 记录错误到错误监控系统
  useEffect(() => {
    if (isChunkLoadError(error.message || error.stack || '')) {
      scheduleChunkErrorReload(error.message || 'chunk error boundary')
      return
    }

    if (pkg !== 'local' && browser) {
      // 同一用户刷新后也持续限流，避免同类错误重复上报刷屏
      if (!shouldReportClientError(error, 'page_error')) {
        return;
      }

      traceError({
        error: {
          message: error.message,
          stack: error.stack,
          digest: error.digest,
        },
        browser: `系统：${browser.getOSName()}@${browser.getOSVersion()};浏览器：${browser.getBrowserName()}@${browser.getBrowserVersion()};引擎：${browser.getEngineName()}userAgent：${window.navigator.userAgent}`,
        page: {
          url: window.location.href,
          type: 'page_error',
        },
        timestamp: new Date().toISOString(),
      });
    }
  }, [error, pkg, browser, traceError])

  return (
    <div className="flex flex-col h-screen w-screen items-center justify-center gap-4">
      <Image src="https://oss.findinai.cn/storage/20250813/427fbb9f3070adb12222d3139d5ea928.svg" width={150} height={150} alt="错误提示" />
      <span className="text-annotation-foreground text-sm">诶呀，遇到了一个错误，再重新试试！{error?.message?.includes('Loading chunk') && (
        <>
          推荐<a
            className="text-annotation-foreground text-sm underline underline-offset-4 cursor-pointer"
            href="https://www.google.cn/chrome/index.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            使用 Chrome 浏览器
          </a>
          访问。
        </>
      )}</span>
      <div className="flex items-center gap-4">
        <Button
          onClick={reset}
          className="min-w-20"
        >
          重试
        </Button>
        <Button
          variant="secondary"
          className="min-w-20"
          onClick={() => window.location.assign('/')}
        >
          返回首页
        </Button>
      </div>
      <div className="absolute bottom-5 flex items-center cursor-pointer">
        <CommunicateWithUs>
          <span className="text-annotation-foreground text-sm">无法解决？联系官方小助手 &gt;</span>
        </CommunicateWithUs>
      </div>
    </div>
  )
}
